// utils/api/AssignmentsApi.js
/**
 * API functions for Assignment System
 */

import { getAuthHeaders } from './ApiUtils';

const API_BASE_URL = '';

// ==================== Assignment APIs ====================

export const createAssignment = async (data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create assignment');
  return json;
};

export const getAssignments = async (filters = {}, classContext) => {
  const params = new URLSearchParams();
  if (filters.type) params.append('type', filters.type);
  if (filters.published !== undefined) params.append('published', filters.published);
  if (filters.active !== undefined) params.append('active', filters.active);

  const url = `${API_BASE_URL}/api/assignments${params.toString() ? '?' + params.toString() : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get assignments');
  return json;
};

export const getAssignment = async (id, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${id}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get assignment');
  return json; // Backend returns { success: true, assignment: {...} }
};

export const lookupAssignment = async (params, classContext) => {
  const query = new URLSearchParams();
  if (params.document_id) query.append('document_id', params.document_id);
  if (params.environment_id) query.append('environment_id', params.environment_id);
  if (params.file_id) query.append('file_id', params.file_id);

  const res = await fetch(`${API_BASE_URL}/api/assignments/lookup?${query.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to lookup assignment');
  return json; // { success: true, assignment: {...} | null }
};

export const updateAssignment = async (assignmentId, data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}`, {
    method: 'PUT',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update assignment');
  return json;
};

export const deleteAssignment = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to delete assignment');
  return json;
};

export const publishAssignment = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}/publish`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to publish assignment');
  return json;
};

export const unpublishAssignment = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}/unpublish`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to unpublish assignment');
  return json;
};

// ==================== Rubric APIs ====================

export const createRubric = async (data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/rubrics`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create rubric');
  return json;
};

export const getRubricByAssignment = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/rubrics/assignment/${assignmentId}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get rubric');
  return json;
};

export const updateRubric = async (rubricId, data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/rubrics/${rubricId}`, {
    method: 'PUT',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update rubric');
  return json;
};

export const deleteRubric = async (rubricId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/rubrics/${rubricId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to delete rubric');
  return json;
};

export const getRubricsByAssignment = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/rubrics/assignment/${assignmentId}/all`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get rubrics');
  return json;
};

// ==================== Submission APIs ====================

export const createSubmission = async (data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create submission');
  return json;
};

export const updateSubmission = async (submissionId, data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/${submissionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update submission');
  return json;
};

export const getAssignmentSubmissions = async (assignmentId, status = null, classContext, { groupByStudent = false } = {}) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (groupByStudent) params.append('group_by_student', 'true');

  const url = `${API_BASE_URL}/api/assignments/submissions/assignment/${assignmentId}${params.toString() ? '?' + params.toString() : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get submissions');
  return json;
};

export const getStudentSubmissionsForAssignment = async (studentId, assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/student/${studentId}?assignment_id=${assignmentId}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get student submissions');
  return json.submissions || [];
};

export const getGradingStats = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/assignment/${assignmentId}/stats`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get stats');
  return json;
};

// ==================== Grading APIs ====================

export const applyRubricItem = async (data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/apply-rubric-item`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to apply rubric item');
  return json;
};

export const removeAppliedRubricItem = async (appliedRubricId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/applied-rubric/${appliedRubricId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to remove rubric item');
  return json;
};

export const updateAppliedRubricItem = async (appliedRubricId, data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/applied-rubric/${appliedRubricId}`, {
    method: 'PUT',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update rubric item');
  return json;
};

export const getAppliedRubricItems = async (submissionId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/submission/${submissionId}/rubric-items`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get applied rubric items');
  return json;
};

export const calculateSubmissionScore = async (submissionId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/submission/${submissionId}/score`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to calculate score');
  return json;
};

export const finalizeGrade = async (submissionId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/submission/${submissionId}/finalize`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to finalize grade');
  return json;
};

// ==================== Feedback APIs ====================

export const getFeedback = async (submissionId, feedbackType = null, classContext) => {
  const params = new URLSearchParams();
  if (feedbackType) params.append('type', feedbackType);

  const url = `${API_BASE_URL}/api/assignments/feedback/submission/${submissionId}${params.toString() ? '?' + params.toString() : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get feedback');
  return json;
};

export const addFeedback = async (data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/feedback`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to add feedback');
  return json;
};

// ==================== Component Submission APIs ====================

export const updateComponentSubmission = async (submissionId, componentType, data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/${submissionId}/component/${componentType}`, {
    method: 'PUT',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update component submission');
  return json;
};

export const getComponentSubmission = async (submissionId, componentType, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/${submissionId}/component/${componentType}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get component submission');
  return json;
};

export const getMySubmissions = async (classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/student/${classContext.username}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get submissions');
  return json;
};

export const getMySubmission = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/student/${classContext.username}?assignment_id=${assignmentId}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get submission');
  // Backend returns { success: true, submissions: [...] }, get the first one
  return json.submissions && json.submissions.length > 0 ? json.submissions[0] : null;
};

// ==================== Component Questions APIs ====================

export const getComponentQuestions = async (assignmentId, componentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}/components/${componentId}/questions`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get component questions');
  return json;
};

export const autoGradeSubmission = async (submissionId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/grading/submission/${submissionId}/auto-grade`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to auto-grade submission');
  return json;
};

// ==================== Submission Actions ====================

export const submitSubmission = async (submissionId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/${submissionId}/submit`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to submit');
  return json;
};

export const getSubmission = async (submissionId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/submissions/${submissionId}`, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get submission');
  return json;
};

// ==================== Regrade Request APIs ====================

export const createRegradeRequest = async (data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/regrade-requests`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create regrade request');
  return json;
};

export const getRegradeRequests = async (filters = {}, classContext) => {
  const params = new URLSearchParams();
  if (filters.assignment_id) params.append('assignment_id', filters.assignment_id);
  if (filters.student_id) params.append('student_id', filters.student_id);
  if (filters.status) params.append('status', filters.status);

  const url = `${API_BASE_URL}/api/assignments/regrade-requests${params.toString() ? '?' + params.toString() : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to get regrade requests');
  return json;
};

export const updateRegradeStatus = async (requestId, data, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/regrade-requests/${requestId}/status`, {
    method: 'PUT',
    headers: getAuthHeaders(classContext),
    credentials: 'include',
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update regrade status');
  return json;
};

// ==================== Batch Operations ====================

export const batchFinalizeGrades = async (assignmentId, classContext) => {
  const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}/finalize-all`, {
    method: 'POST',
    headers: getAuthHeaders(classContext),
    credentials: 'include'
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to release grades');
  return json;
};
